import { brand, neutral, semantic } from "@/constants/colors";
import { fonts } from "@/constants/fonts";
import { Ionicons } from "@expo/vector-icons";
import type React from "react";
import type { ReactNode } from "react";
import { useRef, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Pressable,
  StyleSheet,
  type TextStyle,
  TouchableOpacity,
  useColorScheme,
  View,
  type ViewStyle,
} from "react-native";
import ThemedText from "../core/ThemedText";

type SplitButtonVariant = "filled" | "outline" | "ghost";
type SplitButtonSize = "sm" | "md" | "lg";
type SplitButtonColor =
  | "primary"
  | "success"
  | "warning"
  | "error"
  | "neutral";

export interface SplitButtonAction {
  label: string;
  value: string;
  icon?: ReactNode;
  disabled?: boolean;
}

interface SplitButtonProps {
  onPress?: () => void;
  onActionSelect?: (value: string) => void;
  actions: SplitButtonAction[];
  variant?: SplitButtonVariant;
  size?: SplitButtonSize;
  color?: SplitButtonColor;
  disabled?: boolean;
  loading?: boolean;
  children?: ReactNode;
  style?: ViewStyle;
  textStyle?: TextStyle;
  icon?: ReactNode;
}

export const SplitButton: React.FC<SplitButtonProps> = ({
  onPress,
  onActionSelect,
  actions,
  variant = "filled",
  size = "md",
  color = "primary",
  disabled = false,
  loading = false,
  children,
  style,
  textStyle,
  icon,
}) => {
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";
  const [showMenu, setShowMenu] = useState(false);
  const buttonRef = useRef<View>(null);

  const sizeStyles: Record<
    SplitButtonSize,
    { height: number; fontSize: number; padding: number }
  > = {
    sm: { height: 36, fontSize: 14, padding: 12 },
    md: { height: 44, fontSize: 16, padding: 16 },
    lg: { height: 52, fontSize: 18, padding: 20 },
  };

  const getButtonColor = () => {
    switch (color) {
      case "primary":
        return isDark ? brand.primary.dark : brand.primary.main;
      case "success":
        return isDark ? semantic.success.dark : semantic.success.main;
      case "warning":
        return semantic.warning.main;
      case "error":
        return isDark ? semantic.error.dark : semantic.error.main;
      case "neutral":
        return isDark ? neutral[600] : neutral[300];
    }
  };

  const getVariantStyle = (): ViewStyle => {
    const baseStyle: ViewStyle = {
      borderRadius: 12,
      flexDirection: "row",
      alignItems: "center",
      overflow: "hidden",
    };

    const buttonColor = getButtonColor();

    switch (variant) {
      case "filled":
        return {
          ...baseStyle,
          backgroundColor: buttonColor,
          borderWidth: 1,
          borderColor: buttonColor,
        };
      case "outline":
        return {
          ...baseStyle,
          backgroundColor: "transparent",
          borderColor: buttonColor,
          borderWidth: 1,
        };
      case "ghost":
        return {
          ...baseStyle,
          backgroundColor: "transparent",
        };
    }
  };

  const isLightColor = (hexColor: string) => {
    const r = Number.parseInt(hexColor.slice(1, 3), 16);
    const g = Number.parseInt(hexColor.slice(3, 5), 16);
    const b = Number.parseInt(hexColor.slice(5, 7), 16);

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
  };

  const getTextColor = () => {
    if (disabled) return isDark ? neutral[500] : neutral[400];

    const buttonColor = getButtonColor();

    switch (variant) {
      case "filled":
        if (color === "primary") {
          return "#FFFFFF";
        }
        return isLightColor(buttonColor) ? neutral[900] : "#FFFFFF";
      case "outline":
      case "ghost":
        return isDark ? "#FFFFFF" : "#000000";
    }
  };

  const getSeparatorColor = () => {
    const textColor = getTextColor();
    return textColor + "40"; // 25% opacity
  };

  const handleActionSelect = (value: string) => {
    setShowMenu(false);
    onActionSelect?.(value);
  };

  const menuBackgroundColor = isDark ? neutral[800] : "#FFFFFF";
  const menuTextColor = isDark ? "#FFFFFF" : "#000000";
  const menuBorderColor = isDark ? neutral[700] : neutral[200];

  return (
    <>
      <View style={[getVariantStyle(), { height: sizeStyles[size].height }, style]}>
        {/* Main Button */}
        <Pressable
          onPress={onPress}
          disabled={disabled || loading}
          style={({ pressed }) => [
            styles.mainButton,
            {
              paddingHorizontal: sizeStyles[size].padding,
              opacity: disabled ? 0.5 : pressed ? 0.7 : 1,
            },
          ]}
        >
          {loading ? (
            <ActivityIndicator color={getTextColor()} />
          ) : (
            <View style={styles.contentContainer}>
              {icon && <View style={styles.iconContainer}>{icon}</View>}
              {children && (
                <ThemedText
                  style={{
                    fontSize: sizeStyles[size].fontSize,
                    color: getTextColor(),
                    fontFamily: fonts.semiBold,
                  }}
                >
                  {children}
                </ThemedText>
              )}
            </View>
          )}
        </Pressable>

        {/* Separator */}
        <View
          style={[styles.separator, { backgroundColor: getSeparatorColor() }]}
        />

        {/* Dropdown Button */}
        <Pressable
          ref={buttonRef}
          onPress={() => setShowMenu(!showMenu)}
          disabled={disabled || loading}
          style={({ pressed }) => [
            styles.dropdownButton,
            {
              width: sizeStyles[size].height,
              opacity: disabled ? 0.5 : pressed ? 0.7 : 1,
            },
          ]}
        >
          <Ionicons
            name={showMenu ? "chevron-up" : "chevron-down"}
            size={20}
            color={getTextColor()}
          />
        </Pressable>
      </View>

      {/* Dropdown Menu */}
      <Modal
        visible={showMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setShowMenu(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowMenu(false)}
        >
          <View style={styles.menuContainer}>
            <View
              style={[
                styles.menu,
                {
                  backgroundColor: menuBackgroundColor,
                  borderColor: menuBorderColor,
                },
              ]}
            >
              {actions.map((action, index) => (
                <View key={action.value}>
                  {index > 0 && (
                    <View
                      style={[
                        styles.menuSeparator,
                        { backgroundColor: menuBorderColor },
                      ]}
                    />
                  )}
                  <TouchableOpacity
                    onPress={() => handleActionSelect(action.value)}
                    disabled={action.disabled}
                    style={[
                      styles.menuItem,
                      action.disabled && styles.menuItemDisabled,
                    ]}
                  >
                    {action.icon && (
                      <View style={styles.menuIconContainer}>{action.icon}</View>
                    )}
                    <ThemedText
                      style={[
                        styles.menuItemText,
                        {
                          color: action.disabled
                            ? isDark
                              ? neutral[600]
                              : neutral[400]
                            : menuTextColor,
                        },
                      ]}
                    >
                      {action.label}
                    </ThemedText>
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          </View>
        </TouchableOpacity>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  mainButton: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  contentContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  iconContainer: {
    marginRight: 8,
  },
  separator: {
    width: 1,
    height: "100%",
  },
  dropdownButton: {
    alignItems: "center",
    justifyContent: "center",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  menuContainer: {
    minWidth: 200,
    maxWidth: 300,
  },
  menu: {
    borderRadius: 12,
    borderWidth: 1,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  menuItemDisabled: {
    opacity: 0.5,
  },
  menuIconContainer: {
    marginRight: 12,
  },
  menuItemText: {
    fontSize: 16,
    fontFamily: fonts.medium,
  },
  menuSeparator: {
    height: 1,
  },
});

export default SplitButton;
